/**
 * Notification centre — the history of everything the system sent this account.
 *
 * `unread_count` is computed independently of `limit`/`offset`, which is what makes the
 * bell badge honest on page 1 of a long history. Paging is limit/offset with an explicit
 * «Показать ещё», not an infinite scroll: this list is read to find one specific event,
 * and an endless list has no bottom to reach.
 */
import { apiRequest } from '@/shared/api/client';

export interface ConsumerNotification {
  id: string;
  /** Producer-assigned type, e.g. `PASSWORD_RESET`, `ASSIGNED`, `ARRIVED`. */
  kind: string;
  /** Delivery channel: `PUSH` / `EMAIL` / `SMS` / `IN_APP`. */
  channel: string;
  title: string | null;
  /** Always absent for EMAIL rows — email bodies may embed secret links. */
  body: string | null;
  /** Present for order-event kinds; lets a row deep-link without a second round-trip. */
  order_id: string | null;
  read_at: string | null;
  /** When the delivery worker actually sent it; null while queued or dead-lettered. */
  delivered_at: string | null;
  created_at: string;
}

export interface NotificationsPage {
  notifications: ConsumerNotification[];
  total: number;
  unread_count: number;
  limit: number;
  offset: number;
}

export const NOTIFICATIONS_PAGE_SIZE = 30;

export function fetchNotifications(
  limit: number = NOTIFICATIONS_PAGE_SIZE,
  offset = 0,
): Promise<NotificationsPage> {
  return apiRequest<NotificationsPage>(
    `/v1/consumer/notifications?limit=${limit}&offset=${offset}`,
  );
}

export function markNotificationRead(id: string): Promise<void> {
  return apiRequest<void>(`/v1/consumer/notifications/${id}/read`, { method: 'POST' });
}

export function markAllNotificationsRead(): Promise<void> {
  return apiRequest<void>('/v1/consumer/notifications/read-all', { method: 'POST' });
}
